// services/chatActions.ts
//
// The confirm endpoint for a proposed action (ORA-435 spec, "Tool registry" →
// action tools; backend ORA-445).
//
// THE CONTRACT THIS CLIENT IS WRITTEN AGAINST — ORA-445 must match it, or bump
// it and tell this file:
//
//   POST /api/v5/chat/actions/confirm
//   body: { actionId: string }
//
//   POST /api/v5/chat/actions/dismiss
//   body: { actionId: string }
//   200 { ok: true } — or `ok: false` with a code, read the same way
//
//   POST /api/v5/chat/actions/undo
//   body: { actionId: string }
//   200 { ok: true, summary?, target?, result?, alreadyUndone? }
//
//   200 { ok: true,  summary?: string, target?: { route, params? },
//                     result?: { kind: 'support_sent', topic, conversationId, url? }
//                            |  { kind: 'open_support', topic, prefill } }
//   200 { ok: false, code: string, message?: string }
//   4xx/5xx with { code?, message? } — treated exactly like `ok: false`
//
// Notes on the shape, and why:
//
// - Dismissing has to reach the server, not just the card (ORA-750 review,
//   blocker 1). The runtime reads the student's next message for an
//   affirmation and auto-confirms whatever is still PROPOSED, so a card the
//   student declined and a later "ok" would have sent the message they had just
//   refused. Pressing Skip closes the row; the card clears itself immediately
//   either way, because the student's answer is not a request to wait.
// - `actionId` is the only thing sent. The args the student agreed to are the
//   ones the server recorded when it emitted `action.proposed`; re-sending them
//   from the client would let a tampered request execute something the summary
//   never described.
// - Confirming is free: it does not spend a chat, so nothing here touches the
//   daily limits in the chat slice.
// - A proposal now outlives the turn that proposed it (ORA-750): the client no
//   longer drops it when the next turn opens, because students answer a
//   confirmation by typing "yes". Expiry is therefore the server's alone — it
//   answers a dead proposal with `ok: false` and the code `proposal_expired`,
//   and tells the client through `action.resolved` when it closes one itself.
// - `target` is optional and uses the same named-route vocabulary as a block's
//   `target` (`ChatRouteId` + params), so the success state can offer a way
//   into whatever the action created — the QBank block, for `create_qbank_block`.
// - `result` is an action's own outcome, for the actions that produce something
//   the client must act on rather than navigate to (ORA-590). Today that is
//   `contact_support`: since ORA-743 it reports `support_sent`, meaning the
//   message has already reached Intercom and the card only has to say so. The
//   older `open_support` shape — the message handed back for the client to put
//   into the composer — is still read so this client works against a backend
//   that has not shipped the send. It is read defensively either way: an unknown
//   `kind`, or a result missing the one field that makes it useful, becomes
//   `null` rather than reaching the card.

import axiosInstance from './axiosCreator';
import type { ChatActionUndo, ChatNavigationTarget } from '../types/chatStream';
import { readChatActionResult } from './chatActionPayload';

export { readChatActionResult, readChatActionUndo } from './chatActionPayload';

export const CHAT_ACTION_CONFIRM_ROUTE = '/api/v5/chat/actions/confirm';
export const CHAT_ACTION_DISMISS_ROUTE = '/api/v5/chat/actions/dismiss';
export const CHAT_ACTION_UNDO_ROUTE = '/api/v5/chat/actions/undo';

/**
 * `contact_support`, since ORA-743: the message has ALREADY been sent, and a
 * support conversation is open on the student's account. There is nothing for
 * the client to do with it — it exists so the card can say so honestly, and so
 * an older client that only knows `open_support` does not open an empty
 * composer on top of a message that already went.
 *
 * `conversationId` and `url` are operator-only and must never be rendered.
 */
export interface ChatActionSupportSentResult {
  kind: 'support_sent';
  topic: string | null;
  conversationId: string;
  url?: string;
}

/**
 * `contact_support` on a pre-ORA-743 backend: confirming had no server-side
 * effect and handed back the message for the client to put into Intercom's
 * composer. Kept so this client works against a backend that has not shipped
 * the send yet; nothing in `ora-be` produces it any more.
 */
export interface ChatActionOpenSupportResult {
  kind: 'open_support';
  topic: string | null;
  prefill: string;
}

/** A study-state mutation that requires the surrounding app to refresh. */
export interface ChatActionStudyStateResult {
  kind: 'plan_change' | 'study_change';
  undo?: ChatActionUndo;
  [key: string]: unknown;
}

/** Everything an action can hand back for the client to act on. */
export type ChatActionResult =
  | ChatActionSupportSentResult
  | ChatActionOpenSupportResult
  | ChatActionStudyStateResult;

export type ChatActionConfirmResult =
  | {
      ok: true;
      summary: string | null;
      target: ChatNavigationTarget | null;
      result: ChatActionResult | null;
    }
  | { ok: false; code: string; message: string | null };

export type ChatActionUndoResult =
  | {
      ok: true;
      summary: string | null;
      target: ChatNavigationTarget | null;
      result: ChatActionResult | null;
      alreadyUndone: boolean;
    }
  | { ok: false; code: string; message: string | null };

interface ChatActionConfirmResponse {
  ok?: boolean;
  summary?: string | null;
  target?: ChatNavigationTarget | null;
  result?: unknown;
  code?: string | null;
  message?: string | null;
  alreadyUndone?: boolean;
}

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const readErrorBody = (error: unknown): ChatActionConfirmResponse => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  return typeof data === 'object' && data !== null ? data : {};
};

/**
 * Codes that mean the row is closed, whatever else they say. The student asked
 * for it gone and it is gone; there is nothing to report and nothing to log.
 */
const CLOSED_BY_DISMISS_CODES = new Set([
  'proposal_expired',
  'proposal_not_found',
]);

const dismissOnce = async (
  actionId: string
): Promise<{ ok: boolean; code: string | null; retry: boolean }> => {
  try {
    const response = await axiosInstance.post<ChatActionConfirmResponse>(
      CHAT_ACTION_DISMISS_ROUTE,
      { actionId }
    );

    const body = response.data ?? {};
    const code = asString(body.code);

    if (body.ok !== false) return { ok: true, code: null, retry: false };

    return code !== null && CLOSED_BY_DISMISS_CODES.has(code)
      ? { ok: true, code, retry: false }
      : { ok: false, code: code ?? 'dismiss_failed', retry: false };
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;

    // A proposal the server has never heard of is as closed as one it just
    // closed, and a build without the route yet is not a student-facing
    // problem: the card has already gone from their screen.
    if (status === 404)
      return { ok: true, code: 'proposal_not_found', retry: false };

    // A dropped request or a server that fell over is worth one more go: the
    // whole point of this call is that the row must not stay PROPOSED, and the
    // student is not waiting on it.
    const isRetryable = status === undefined || status >= 500;

    return {
      ok: false,
      code: asString(readErrorBody(error).code),
      retry: isRetryable,
    };
  }
};

/**
 * Decline a proposed action, so the row stops being PROPOSED server-side.
 *
 * Never throws, and reports only what the caller can act on: whether the row is
 * closed, and the code when it is not. A closed row covers `ok: true`, an
 * already-dismissed one (also `ok: true`), a 404, and `proposal_expired` — all
 * of them leave nothing to confirm.
 *
 * One retry, because nothing is waiting on this: the student's card is already
 * gone, and leaving the row open is what lets their next "ok" run an action they
 * declined. After that the server's own expiry is the backstop.
 */
export const dismissChatAction = async (
  actionId: string
): Promise<{ ok: boolean; code: string | null }> => {
  const first = await dismissOnce(actionId);
  if (first.ok || !first.retry) return { ok: first.ok, code: first.code };

  const second = await dismissOnce(actionId);

  return { ok: second.ok, code: second.code };
};

/**
 * Confirm a proposed action. Never throws: the card shows the outcome either
 * way, and a failed action must not take the conversation down with it.
 */
export const confirmChatAction = async (
  actionId: string
): Promise<ChatActionConfirmResult> => {
  try {
    const response = await axiosInstance.post<ChatActionConfirmResponse>(
      CHAT_ACTION_CONFIRM_ROUTE,
      { actionId }
    );

    const body = response.data ?? {};

    if (body.ok === false) {
      return {
        ok: false,
        code: asString(body.code) ?? 'action_failed',
        message: asString(body.message),
      };
    }

    return {
      ok: true,
      summary: asString(body.summary),
      target: body.target ?? null,
      result: readChatActionResult(body.result),
    };
  } catch (error) {
    const body = readErrorBody(error);

    return {
      ok: false,
      code: asString(body.code) ?? 'action_failed',
      message: asString(body.message),
    };
  }
};

/** Undo an executed action. Like confirm, a refusal is card state, not a throw. */
export const undoChatAction = async (
  actionId: string
): Promise<ChatActionUndoResult> => {
  try {
    const response = await axiosInstance.post<ChatActionConfirmResponse>(
      CHAT_ACTION_UNDO_ROUTE,
      { actionId }
    );

    const body = response.data ?? {};

    if (body.ok === false) {
      return {
        ok: false,
        code: asString(body.code) ?? 'action_failed',
        message: asString(body.message),
      };
    }

    return {
      ok: true,
      summary: asString(body.summary),
      target: body.target ?? null,
      result: readChatActionResult(body.result),
      alreadyUndone: body.alreadyUndone === true,
    };
  } catch (error) {
    const body = readErrorBody(error);

    return {
      ok: false,
      code: asString(body.code) ?? 'action_failed',
      message: asString(body.message),
    };
  }
};
